import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Svg,
  Path,
  Font,
  Link,
} from '@react-pdf/renderer';
import type { Style } from '@react-pdf/types';
import format from 'date-fns-tz/format';
import {
  EmployeeWaiverQuestions,
  GetWaiversData,
} from '../../../types/waivers';
import { getFullName } from '../../../helpers/global';
import { LoadedUser } from '../../../interfaces';

Font.register({
  family: 'Montserrat',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Ew-.ttf',
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCu170w-.ttf',
      fontWeight: 'semibold',
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v26/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCuM70w-.ttf',
      fontWeight: 'bold',
    },
  ],
});

const PAGE_VERTICAL_PADDING = 32;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Montserrat',
    flexDirection: 'column',
    gap: 32,
    backgroundColor: '#FFFFFF',
    paddingVertical: PAGE_VERTICAL_PADDING,
  },
  section: {
    paddingHorizontal: 16,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  header: {
    textAlign: 'center',
    width: '100%',
    fontWeight: 'bold',
    fontSize: 24,
  },
  sectionHeader: {
    textAlign: 'center',
    width: '100%',
    fontWeight: 'semibold',
    fontSize: 18,
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingRight: 32,
  },
  checkbox: {
    width: 14,
    height: 14,
    aspectRatio: 1,
    borderRadius: 2,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#000',
    marginRight: 5,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  checkmark: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    bottom: -1,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  spacer: {
    height: 10,
  },
  text: {
    fontSize: 12,
  },
  textField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  textFieldInput: {
    border: '0.5px solid #000',
    padding: 5,
    width: '100%',
    borderRadius: 1,
    minHeight: 150,
  },
  idSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footer: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
  },
  footerText: {
    fontSize: 8,
    textAlign: 'center',
    textDecoration: 'none',
    color: '#333',
  },
});

function Section({
  header,
  title,
  children,
  style,
  wrap = true,
}: React.PropsWithChildren<{
  header?: string;
  title?: string;
  style?: Style;
  wrap?: boolean;
}>) {
  return (
    <View style={{ ...styles.section, ...style }} wrap={wrap}>
      {header && (
        <Text style={styles.sectionHeader} wrap={false}>
          {header}
        </Text>
      )}
      {title && (
        <Text style={styles.text} wrap={false}>
          {title}
        </Text>
      )}
      {children}
    </View>
  );
}

function Checkbox({ checked, label }: { checked: boolean; label: string }) {
  return (
    <View style={[styles.checkboxContainer]} wrap={false}>
      <View
        style={[
          styles.checkbox,
          {
            borderColor: checked ? '#333' : '#000',
            backgroundColor: checked ? '#333' : 'transparent',
          },
        ]}>
        {checked && (
          <Svg viewBox="0 0 24 24" style={styles.checkmark}>
            <Path
              d="M20.285 4.285a1.003 1.003 0 00-1.415 0L9 14.15l-3.87-3.87a1.003 1.003 0 00-1.415 1.415l4.585 4.585a1.003 1.003 0 001.415 0l10.585-10.585a1.003 1.003 0 000-1.415z"
              fill={checked ? '#fff' : '#000'}
            />
          </Svg>
        )}
      </View>
      <Text style={styles.text}>{label}</Text>
    </View>
  );
}

function TextField({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.textField} wrap={false}>
      <Text style={styles.text} wrap={false}>
        {label}
      </Text>
      <Text style={{ ...styles.text, ...styles.textFieldInput }}>{value}</Text>
    </View>
  );
}

function Signature({ label, src }: { label: string; src: string }) {
  return (
    <View style={styles.textField} wrap={false}>
      <Text style={styles.text} wrap={false}>
        {label}
      </Text>
      <Image
        src={src}
        style={{ width: 100, height: 100, objectFit: 'contain' }}
      />
    </View>
  );
}

interface Props {
  waiverForm: EmployeeWaiverQuestions;
  waiver: GetWaiversData;
  user: LoadedUser;
}

function EmployeeWaiverPDF({ waiverForm, waiver, user }: Props) {
  const options = waiver.employeeSettings?.options || [];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View fixed style={styles.footer}>
          <Link style={styles.footerText} href="https://getporter.io">
            Powered by Porter
          </Link>
        </View>

        <Section header="Employee Section">
          <Text style={styles.text} wrap={false}>
            Submitted By:{' '}
            {user?.employee.displayName || (user ? getFullName(user) : '')}
          </Text>
          <Text style={styles.text} wrap={false}>
            {/* include timezone */}
            Submitted On: {format(new Date(), 'MM/dd/yyyy h:mm a zzz')}
          </Text>
        </Section>

        <Section>
          {options.map(option => {
            const value = (waiverForm as Record<string, unknown>)[option.id];
            return option.type === 'CHECKBOX' ? (
              <Checkbox
                key={option.id}
                label={option.text}
                checked={value as boolean}
              />
            ) : option.type === 'TEXT' || option.type === 'MULTILINE_TEXT' ? (
              <TextField
                key={option.id}
                label={option.text}
                value={value as string}
              />
            ) : option.type === 'SIGNATURE' ? (
              <Signature
                key={option.id}
                label={option.text}
                src={value as string}
              />
            ) : null;
          })}
        </Section>
      </Page>
    </Document>
  );
}

export default EmployeeWaiverPDF;
